import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const LEER = { name: "", text: "", sterne: 5, datum: new Date().toISOString().slice(0, 10), sichtbar: true };

export default function BewertungenTab({ pwd }) {
  const headers = { "X-Admin-Password": pwd };
  const [list, setList] = useState([]);
  const [url, setUrl] = useState("");
  const [neu, setNeu] = useState(LEER);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/google-reviews`, { headers });
      setList(data.reviews || []);
      setUrl(data.google_url || "");
    } catch {
      toast.error("Bewertungen konnten nicht geladen werden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const speichern = async () => {
    if (!neu.name.trim() || !neu.text.trim()) { toast.error("Bitte Name und Text eintragen"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/google-reviews`, neu, { headers });
      setNeu(LEER);
      await load();
      toast.success("Bewertung gespeichert");
    } catch (err) { toast.error("Fehler: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const umschalten = async (r) => {
    await axios.put(`${API}/admin/google-reviews/${r.id}`, { ...r, sichtbar: !r.sichtbar }, { headers });
    load();
  };

  const loeschen = async (r) => {
    if (!window.confirm(`Bewertung von ${r.name} löschen?`)) return;
    await axios.delete(`${API}/admin/google-reviews/${r.id}`, { headers });
    load();
  };

  const urlSpeichern = async () => {
    await axios.put(`${API}/admin/google-reviews-profil`, { google_url: url }, { headers });
    toast.success("Google-Link gespeichert");
  };

  return (
    <div data-testid="bewertungen-tab" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Google-Bewertungen</p>
        <p className="text-sm text-[#757067] mt-1 max-w-2xl">
          Trag hier deine besten Google-Bewertungen ein. Sichtbare erscheinen dezent auf der Startseite —
          mit Sternen, Text und einem ruhigen Hinweis „Google". Du entscheidest, welche zu sehen sind.
        </p>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Link zu deinem Google-Profil</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[260px]">
            <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="bewertung-google-url"
              placeholder="https://www.google.com/…" className={IN} />
          </div>
          <button onClick={urlSpeichern} data-testid="bewertung-url-save"
            className="border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            <Save size={14} /> Speichern
          </button>
        </div>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Neue Bewertung</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className={LB}>Name wie bei Google</label>
            <input value={neu.name} onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))}
              data-testid="bewertung-name" placeholder="z. B. Hotel Zentlinde" className={IN} />
          </div>
          <div>
            <label className={LB}>Sterne</label>
            <select value={neu.sterne} onChange={(e) => setNeu((n) => ({ ...n, sterne: Number(e.target.value) }))}
              data-testid="bewertung-sterne" className={IN}>
              {[5, 4, 3, 2, 1].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={LB}>Datum</label>
            <input type="date" value={neu.datum} onChange={(e) => setNeu((n) => ({ ...n, datum: e.target.value }))}
              data-testid="bewertung-datum" className={IN} />
          </div>
          <div className="md:col-span-4">
            <label className={LB}>Text der Bewertung</label>
            <textarea rows={3} value={neu.text} onChange={(e) => setNeu((n) => ({ ...n, text: e.target.value }))}
              data-testid="bewertung-text" className={IN} />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={speichern} disabled={busy} data-testid="bewertung-save"
            className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Bewertung übernehmen
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {list.length === 0 ? (
          <p className="text-sm text-[#757067]" data-testid="bewertungen-leer">Noch keine Google-Bewertung eingetragen.</p>
        ) : list.map((r) => (
          <div key={r.id} data-testid={`bewertung-row-${r.id}`}
            className="border border-[#E3DECE] bg-white px-4 py-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-[240px] flex-1">
              <p className="text-sm font-medium">
                {r.name} <span className="text-[#BA6D52] tracking-[0.2em]">{"★".repeat(r.sterne)}</span>
                {!r.sichtbar && <span className="text-[11px] text-[#757067] ml-2">· ausgeblendet</span>}
              </p>
              <p className="text-xs text-[#575249] mt-1 whitespace-pre-wrap">{r.text}</p>
              <p className="text-[11px] text-[#9A948A] mt-1">{r.datum}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => umschalten(r)} data-testid={`bewertung-toggle-${r.id}`}
                className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-3 py-2 inline-flex items-center gap-2">
                {r.sichtbar ? <EyeOff size={13} /> : <Eye size={13} />} {r.sichtbar ? "Ausblenden" : "Zeigen"}
              </button>
              <button onClick={() => loeschen(r)} data-testid={`bewertung-del-${r.id}`}
                className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
